import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import JustWatchTVPlayBtn from '@/pages/titleDetailV1/v1/hero/JustwatchTVPlayBtn.vue';
import TitleDetailInfoRow from '@/pages/titleDetailV1/components/titleDetailInfoRow.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { OfferLocation } from '@/helpers/offer-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { MonetizationType, SraPlacement } from '@/@types/graphql-types';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import TrailerModal from '@/components/TrailerModal.vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';


export default /*#__PURE__*/_defineComponent({
  __name: 'AVODHeroSection',
  props: {
    title: null,
    additionalContexts: null
  },
  setup(__props: any) {

const props = __props as {
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
};



const languageStore = useLanguageStore();
const appStore = useAppStore();

const titleName = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.title : props.title.content.title;
});

const description = computed(() => {
	return props.title.content.shortDescription || '';
});

const imageUrls = computed(() => props.title.content.backdrops.map(backdrop => backdrop.backdropUrl));

const isJustWatchTV = computed(
	() =>
		props.title.justwatchTVOffers.length > 0 &&
		props.title.justwatchTVOffers[0].monetizationType === MonetizationType.Free &&
		props.title.justwatchTVOffers[0].package.technicalName === 'justwatchtv'
);

const { sponsoredAd } = useSendBidId({
	pageType: 'VIEW_TITLE_DETAIL',
	// AVOD is always JWTV
	placement: SraPlacement.JwtvPreroll,
});

const titleClips = computed(() => {
	const JustWatchTVClip = isJustWatchTV.value
		? [
				{
					provider: 'JUSTWATCH_TV',
					externalId: props.title.justwatchTVOffers[0].id,
					sourceUrl:
						props.title.justwatchTVOffers[0].streamUrlExternalPlayer ??
						props.title.justwatchTVOffers[0].streamUrl,
				},
		  ]
		: null;

	return JustWatchTVClip || [];
});

// Modal state
const hasModalOpened = ref(false);
const isModalOpening = ref(false);

// Offer for impression tracking
const offerForImpression = computed(() => {
	if (isJustWatchTV.value) return props.title.justwatchTVOffers[0];
	return null;
});

// Clickout URL for tracking
const clickoutUrl = computed(() => {
	if (!offerForImpression.value) return null;

	const objectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const objectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;

	return new ClickoutUrl(offerForImpression.value)
		.setTitleSourceId(props.title?.id ?? null)
		.addContexts(new SnowplowTitleContextGraphql(objectId, objectType))
		.addContexts(props.additionalContexts)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.set('uct_country', languageStore.country)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'video_player')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title?.id ?? null)
		.setCanary(appStore.xServeTrack);
});

// Play Button Click Handler (same as handleBackdropClick)
function handlePlayClick() {
	if (clickoutUrl.value) {
		trackClickout(clickoutUrl.value);
	}

	openTrailerModal(false);
}

// Open trailer modal
async function openTrailerModal(nonInteraction: boolean) {
	if (titleClips.value.length === 0) return;
	if (hasModalOpened.value || isModalOpening.value) return;
	isModalOpening.value = true;

	try {
		hasModalOpened.value = true;
		await new Promise(resolve => setTimeout(resolve, 0));

		TrackingHelper.trackEvent('youtube_started', {
			action: props.title.objectType.toLowerCase(),
			label: 'backdrop',
			value: 5,
			nonInteraction,
		});

		await ModalHelper.openModal(
			TrailerModal,
			{
				clips: titleClips,
				title: props.title,
				source: 'backdrop',
				additionalContexts: props.additionalContexts,
				playerAdditionalContexts: getClickoutUrlContextsArray(clickoutUrl.value),
				sponsoredAd: sponsoredAd.value,
				sraPlacement: SraPlacement.JwtvPreroll,
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[],
			() => (hasModalOpened.value = false)
		);
	} catch (e) {
		console.error(e);
	} finally {
		isModalOpening.value = false;
		hasModalOpened.value = false;
	}
}

// Read More Functionality
const isDescriptionExpanded = ref(false);
const showReadMoreButton = ref(false);
const descriptionRef = ref<HTMLElement | null>(null);

// Visibility tracking
const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

function onVisibilityChanged(isVisible: boolean) {
	if (!!clickoutUrl.value) onIntersection(clickoutUrl.value, isVisible);
}

const observeVisibilityOptions = computed(() => {
	return !!offerForImpression.value
		? {
				callback: onVisibilityChanged,
				...observableOptions,
		  }
		: null;
});

onMounted(() => {
	// Check if description exceeds 3 lines
	if (descriptionRef.value) {
		const isClamped = descriptionRef.value.scrollHeight > descriptionRef.value.clientHeight;
		showReadMoreButton.value = isClamped;
	}
});

function toggleDescription() {
	isDescriptionExpanded.value = !isDescriptionExpanded.value;
	TrackingHelper.trackEvent('userinteraction', {
		action: isDescriptionExpanded.value ? 'read_more_clicked' : 'read_less_clicked',
		label: 'synopsis',
	});
}

return { __sfc: true,props, languageStore, appStore, titleName, description, imageUrls, isJustWatchTV, sponsoredAd, titleClips, hasModalOpened, isModalOpening, offerForImpression, clickoutUrl, handlePlayClick, openTrailerModal, isDescriptionExpanded, showReadMoreButton, descriptionRef, onIntersection, onVisibilityChanged, observeVisibilityOptions, toggleDescription, JustWatchTVPlayBtn, TitleDetailInfoRow, PictureComp }
}

})